// Office Tasks — staff-only interoffice task scheduler (Phase 3). List (overdue-first) with filters,
// a detail panel (description, status/priority/due controls, multi-assignee picker, append-only
// activity timeline, a comment box, and Mark complete / Reopen), and a New-task modal. Internal
// tool; students/clients never see it (staff-gated route + server requireRole). NAMING: this is the
// interoffice "Tasks" module (`office_tasks`) — never the bookings Task.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import { C, F, card, Btn, ErrorText } from '../components/admin/adminUi';
import AppHeader from '../components/AppHeader';
import PersonCombobox, { type PersonOption } from '../components/ops-board/PersonCombobox';
import { useIsMobile } from '../lib/useIsMobile';
import * as api from '../lib/tasks';

const STATUS_LABELS: Record<api.OfficeTaskStatus, string> = {
  open: 'Open', in_progress: 'In progress', blocked: 'Blocked', done: 'Done', cancelled: 'Cancelled',
};
const STATUS_ORDER: api.OfficeTaskStatus[] = ['open', 'in_progress', 'blocked', 'done', 'cancelled'];

const inputStyle: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', color: C.text };
const fullInput: CSSProperties = { ...inputStyle, width: '100%' };
const labelStyle: CSSProperties = { fontWeight: 600, fontSize: 12, color: C.textDim, marginBottom: 3, display: 'block' };

function fmt(iso: string | null): string {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)); }
  catch { return iso.slice(0, 16).replace('T', ' '); }
}
// Allocated-staff label: "CODE - First Last" (structured name falls back to full_name; codeless
// staff show just the name — never a dangling " - "). Mirrors staffLabel for an assignee/creator
// PersonRef. Staff only — student/client displays keep their own personDisplayLabel handling.
function personRefLabel(p: api.PersonRef): string {
  const namePart = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.full_name;
  const code = p.pilot_code?.trim();
  return code ? `${code} - ${namePart}` : namePart;
}

// "{pilot_code} - {first} {last}"; structured name falls back to full_name; codeless staff show just
// the name — never a dangling " - ".
function staffLabel(s: api.StaffOption): string {
  const namePart = [s.first_name, s.last_name].filter(Boolean).join(' ').trim() || s.full_name;
  const code = s.pilot_code?.trim();
  return code ? `${code} - ${namePart}` : namePart;
}

// Timeline attribution: "(CODE)" from the actor's pilot_code, else "(First Last)"/full name; a null
// actor is system-generated (reminders, email-origin) → "(System)".
function actionActorLabel(a: api.OfficeTaskAction): string {
  if (!a.actor) return '(System)';
  const code = a.actor.pilot_code?.trim();
  if (code) return `(${code})`;
  const namePart = [a.actor.first_name, a.actor.last_name].filter(Boolean).join(' ').trim() || a.actor.full_name;
  return `(${namePart})`;
}

// datetime-local <-> ISO helpers (the picker is wall-clock; the API wants ISO with offset).
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Pill({ text, bg, fg }: { text: string; bg: string; fg: string }) {
  return <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' }}>{text}</span>;
}

// Visibility scope chip (Phase 7). Personal = who's involved; Base = the base; Company = everyone.
function VisibilityChip({ v }: { v: api.OfficeTaskVisibility }) {
  const style: Record<api.OfficeTaskVisibility, { bg: string; fg: string }> = {
    personal: { bg: C.redLight, fg: C.red }, base: { bg: C.amberLight, fg: C.amber }, company: { bg: C.pill, fg: C.textMuted },
  };
  const s = style[v];
  return <Pill text={api.VISIBILITY_LABELS[v]} bg={s.bg} fg={s.fg} />;
}

// Owner-only break-glass friction dialog (Phase 7 §5). Deliberately high-friction copy + a step-up
// password. NOT for routine use — every access is audited + shown in the task's activity.
function BreakGlassDialog({ onClose, onConfirm }: { onClose: () => void; onConfirm: (password: string) => Promise<string | null> }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function go() {
    if (!password) { setErr('Enter your password to confirm.'); return; }
    setBusy(true); setErr('');
    const e = await onConfirm(password);
    setBusy(false);
    if (e) setErr(e);
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '80px 16px', zIndex: 1100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: 460, maxWidth: '100%', fontFamily: F, color: C.text }}>
        <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>🔓 Break-glass access</h2>
        <p style={{ fontSize: 13, color: C.textDim, margin: '0 0 12px', lineHeight: 1.5 }}>
          This access isn't intended for routine use — do you really require it? Every task you open this
          way is recorded in an audit log and shown in the task's own activity.
        </p>
        <label style={labelStyle}>Confirm your password</label>
        <input type="password" value={password} autoFocus onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void go(); }} style={fullInput} />
        <ErrorText>{err}</ErrorText>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" disabled={busy} onClick={go}>Reveal hidden tasks</Btn>
        </div>
      </div>
    </div>
  );
}

interface FilterOption { value: string; label: string }

// Multi-select filter: a button showing the selected count that opens a checkbox popover with Select
// all / Clear. Selecting nothing = no filter. Optional search box filters the option labels (used for
// the assignee/creator lists, which can be long). (Lifted from the Enquiries pattern.)
function FilterMultiSelect({ label, options, selected, onChange, searchable = false }: {
  label: string; options: FilterOption[]; selected: string[]; onChange: (next: string[]) => void; searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const needle = q.trim().toLowerCase();
  const shown = searchable && needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const btnStyle: CSSProperties = { ...inputStyle, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, borderColor: selected.length ? C.accent : C.borderMed, color: selected.length ? C.text : C.textDim };
  const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', fontSize: 13, color: C.text, cursor: 'pointer', borderRadius: 5 };
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={btnStyle}>
        {label}{selected.length ? ` (${selected.length})` : ''} <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', left: 0, minWidth: 200, maxWidth: 320, background: '#fff', border: `1px solid ${C.borderMed}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <button type="button" onClick={() => onChange(options.map((o) => o.value))} style={{ ...inputStyle, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>Select all</button>
            <button type="button" onClick={() => onChange([])} style={{ ...inputStyle, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>Clear</button>
          </div>
          {searchable && (
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: 6 }} />
          )}
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {shown.length === 0 && <div style={{ padding: 6, fontSize: 12, color: C.textMuted }}>No matches.</div>}
            {shown.map((o) => (
              <label key={o.value} style={rowStyle}>
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} style={{ accentColor: C.accent }} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Tasks() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState<api.OfficeTaskListItem[]>([]);
  const [staff, setStaff] = useState<api.StaffOption[]>([]);
  const [bases, setBases] = useState<api.BaseRef[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<api.OfficeTaskDetail | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState('');
  // Break-glass (owner-only, Phase 7). The password is held only for the duration of break-glass mode
  // (cleared on exit) so per-task detail views can audit individually without re-prompting each click.
  const [isOwner, setIsOwner] = useState(false);
  const [bgMode, setBgMode] = useState(false);
  const [bgPassword, setBgPassword] = useState('');
  const [showBg, setShowBg] = useState(false);

  const [searchParams] = useSearchParams();
  const [fStatuses, setFStatuses] = useState<string[]>([]);
  const [fAssignees, setFAssignees] = useState<string[]>(searchParams.get('assignee') === 'me' ? ['me'] : []);
  const [fBases, setFBases] = useState<string[]>([]);
  const [fCreators, setFCreators] = useState<string[]>([]);
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fOverdue, setFOverdue] = useState(false);
  const [fDueWeek, setFDueWeek] = useState(false);
  const [fUnassigned, setFUnassigned] = useState(false);

  // Keyword search — debounced 300ms so typing doesn't fire a request per keystroke.
  const [search, setSearch] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setQDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    const params: api.ListParams = {
      statuses: fStatuses.length ? (fStatuses as api.OfficeTaskStatus[]) : undefined,
      assignees: fAssignees.length ? fAssignees : undefined,
      baseIds: fBases.length ? fBases : undefined,
      q: qDebounced.trim() || undefined,
      creators: fCreators.length ? fCreators : undefined,
      created_from: fFrom || undefined,
      created_to: fTo || undefined,
      tz: (fFrom || fTo) ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
      overdue: fOverdue || undefined,
      due_this_week: fDueWeek || undefined,
      unassigned: fUnassigned || undefined,
    };
    if (bgMode && bgPassword) {
      const r = await api.breakGlassList(bgPassword, params);
      if (r.ok) { setItems(r.data ?? []); return; }
      // Step-up lapsed/failed → drop out of break-glass mode back to the normal view.
      setBgMode(false); setBgPassword('');
    }
    setItems(await api.listTasks(params));
  }, [fStatuses, fAssignees, fBases, qDebounced, fCreators, fFrom, fTo, fOverdue, fDueWeek, fUnassigned, bgMode, bgPassword]);

  const statusOptions = useMemo<FilterOption[]>(() => STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABELS[s] })), []);
  const assigneeOptions = useMemo<FilterOption[]>(
    () => [{ value: 'me', label: 'Assigned to me' }, ...staff.map((s) => ({ value: s.id, label: staffLabel(s) }))],
    [staff],
  );
  const creatorOptions = useMemo<FilterOption[]>(
    () => [{ value: 'me', label: 'Created by me' }, ...staff.map((s) => ({ value: s.id, label: staffLabel(s) }))],
    [staff],
  );
  const baseOptions = useMemo<FilterOption[]>(
    () => [{ value: 'none', label: 'No base' }, ...bases.map((b) => ({ value: b.id, label: `${b.short_code} — ${b.name}` }))],
    [bases],
  );

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.fetchStaff().then(setStaff); api.fetchBases().then(setBases); }, []);
  useEffect(() => { api.fetchSummary().then((s) => setIsOwner(!!s?.is_site_owner)); }, []);

  const openDetail = useCallback(async (id: string) => {
    setSelectedId(id);
    // In break-glass mode, a HIDDEN task is opened via the audited break-glass detail path; visible
    // tasks use the normal read (no needless audit). Outside break-glass mode, always the normal read.
    const hidden = bgMode && items.find((t) => t.id === id)?.hidden;
    if (hidden && bgPassword) { const r = await api.breakGlassTask(id, bgPassword); setDetail(r.ok ? r.data ?? null : null); return; }
    setDetail(await api.getTask(id));
  }, [bgMode, bgPassword, items]);
  const refreshDetail = useCallback(async () => { if (selectedId) setDetail(await api.getTask(selectedId)); }, [selectedId]);

  const exitBreakGlass = useCallback(() => { setBgMode(false); setBgPassword(''); setSelectedId(null); setDetail(null); }, []);

  // Deep-link: /tasks/:id (e.g. from a notification) opens that task's detail on load.
  const { id: routeId } = useParams();
  useEffect(() => { if (routeId) void openDetail(routeId); }, [routeId, openDetail]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: F }}>
      <AppHeader context="Tasks">
        <Link to="/" style={{ color: '#9fb4c7', fontSize: 13, textDecoration: 'none' }}>← Home</Link>
      </AppHeader>
      <main style={{ padding: '20px 18px 60px', maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 18, margin: 0 }}>Tasks</h1>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {isOwner && !bgMode && <Btn kind="ghost" onClick={() => setShowBg(true)}>🔓 Break-glass</Btn>}
            <Btn kind="primary" onClick={() => { setErr(''); setShowNew(true); }}>+ New task</Btn>
          </div>
        </div>

        {bgMode && (
          <div style={{ ...card, marginBottom: 12, background: C.redLight, borderColor: C.red, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13, color: C.red }}>🔓 Break-glass mode</strong>
            <span style={{ fontSize: 12.5, color: C.text }}>Showing all tasks, including ones normally hidden from you. Every task you open is audited.</span>
            <div style={{ marginLeft: 'auto' }}><Btn kind="ghost" onClick={exitBreakGlass}>Exit</Btn></div>
          </div>
        )}

        {/* Filters — keyword search + multi-select facets; empty = show all. */}
        <div style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
          <div><label style={labelStyle}>Search</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Title, description, email…"
              style={{ ...inputStyle, minWidth: 220 }} />
          </div>
          <div><label style={labelStyle}>Status</label>
            <FilterMultiSelect label="Status" options={statusOptions} selected={fStatuses} onChange={setFStatuses} />
          </div>
          <div><label style={labelStyle}>Assignee</label>
            <FilterMultiSelect label="Assignee" options={assigneeOptions} selected={fAssignees} onChange={setFAssignees} searchable />
          </div>
          <div><label style={labelStyle}>Creator</label>
            <FilterMultiSelect label="Creator" options={creatorOptions} selected={fCreators} onChange={setFCreators} searchable />
          </div>
          <div><label style={labelStyle}>Base</label>
            <FilterMultiSelect label="Base" options={baseOptions} selected={fBases} onChange={setFBases} />
          </div>
          <div><label style={labelStyle}>From</label>
            <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} style={inputStyle} />
          </div>
          <div><label style={labelStyle}>To</label>
            <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={inputStyle} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.textDim }}>
            <input type="checkbox" checked={fOverdue} onChange={(e) => setFOverdue(e.target.checked)} style={{ accentColor: C.accent }} /> Overdue
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.textDim }}>
            <input type="checkbox" checked={fDueWeek} onChange={(e) => setFDueWeek(e.target.checked)} style={{ accentColor: C.accent }} /> Due this week
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.textDim }}>
            <input type="checkbox" checked={fUnassigned} onChange={(e) => setFUnassigned(e.target.checked)} style={{ accentColor: C.accent }} /> Unassigned
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 1fr) minmax(360px, 1.3fr)', gap: 16, alignItems: 'start' }}>
          {(!isMobile || !selectedId) && (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {items.length === 0 && <div style={{ padding: 18, color: C.textMuted, fontSize: 13 }}>No tasks match.</div>}
            {items.map((t) => (
              <button key={t.id} onClick={() => openDetail(t.id)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: t.id === selectedId ? C.pill : '#fff', border: 'none', borderBottom: `1px solid ${C.border}`, padding: '11px 14px', cursor: 'pointer', fontFamily: F }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13.5 }}>{t.title}</strong>
                  {t.priority === 'high' && <Pill text="HIGH" bg={C.amberLight} fg={C.amber} />}
                  {t.overdue && <Pill text="OVERDUE" bg={C.redLight} fg={C.red} />}
                  {t.hidden && <Pill text="HIDDEN" bg={C.red} fg="#fff" />}
                  <VisibilityChip v={t.visibility} />
                  {t.base && <Pill text={t.base.short_code} bg={C.pill} fg={C.textMuted} />}
                  <span style={{ marginLeft: 'auto' }}><Pill text={STATUS_LABELS[t.status]} bg={C.pill} fg={C.textMuted} /></span>
                </div>
                {t.description && <div style={{ fontSize: 12.5, color: C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</div>}
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3 }}>
                  {t.due_at ? <span style={{ color: t.overdue ? C.red : C.textMuted, fontWeight: t.overdue ? 700 : 400 }}>Due {fmt(t.due_at)}</span> : 'No due date'}
                  {t.assignees.length ? ` · ${t.assignees.map((a) => personRefLabel(a.user)).join(', ')}` : ' · unassigned'}
                </div>
              </button>
            ))}
          </div>
          )}

          {(!isMobile || selectedId) && (
          <div>
            {isMobile && selectedId && (
              <button onClick={() => { setSelectedId(null); setDetail(null); }}
                style={{ ...inputStyle, cursor: 'pointer', marginBottom: 10, background: '#fff', color: C.accent, fontWeight: 700 }}>← Back to list</button>
            )}
            {detail
              ? <Detail key={detail.id} detail={detail} staff={staff} bases={bases} onChanged={() => { refreshDetail(); load(); }} />
              : <div style={{ ...card, color: C.textMuted, fontSize: 13 }}>{isMobile && selectedId ? 'Loading…' : 'Select a task to view its details.'}</div>}
          </div>
          )}
        </div>
      </main>

      {showNew && (
        <NewTask staff={staff} bases={bases} onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); load(); openDetail(id); }} setErr={setErr} err={err} />
      )}

      {showBg && (
        <BreakGlassDialog onClose={() => setShowBg(false)} onConfirm={async (password) => {
          const r = await api.breakGlassList(password);
          if (!r.ok) return r.error || 'Break-glass failed.';
          setBgPassword(password); setBgMode(true); setItems(r.data ?? []); setShowBg(false); setSelectedId(null); setDetail(null);
          return null;
        }} />
      )}
    </div>
  );
}

// ─── Detail panel ────────────────────────────────────────────────────────────────────
function Detail({ detail, staff, bases, onChanged }: { detail: api.OfficeTaskDetail; staff: api.StaffOption[]; bases: api.BaseRef[]; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [due, setDue] = useState(toLocalInput(detail.due_at));

  const assignedIds = new Set(detail.assignees.map((a) => a.user_id));
  const pickerOptions: PersonOption[] = useMemo(
    () => staff.filter((s) => !assignedIds.has(s.id)).map((s) => ({
      id: s.id, label: staffLabel(s), code: s.pilot_code, first_name: s.first_name, last_name: s.last_name, full_name: s.full_name,
    })),
    [staff, detail.assignees],
  );

  async function patch(p: Parameters<typeof api.patchTask>[1], failMsg: string) {
    setBusy(true); setErr('');
    const r = await api.patchTask(detail.id, p);
    setBusy(false);
    if (!r.ok) setErr(r.error || failMsg); else onChanged();
  }
  async function addAssignee(userId: string) {
    setBusy(true); setErr('');
    const r = await api.addAssignee(detail.id, userId);
    setBusy(false);
    if (!r.ok) setErr(r.error || 'Could not assign.'); else onChanged();
  }
  async function removeAssignee(userId: string) {
    setBusy(true); setErr('');
    const r = await api.removeAssignee(detail.id, userId);
    setBusy(false);
    if (!r.ok) setErr(r.error || 'Could not unassign.'); else onChanged();
  }
  async function saveDue() {
    await patch({ due_at: due ? new Date(due).toISOString() : null }, 'Could not update the due date.');
  }
  async function submitNote() {
    setErr('');
    if (!note.trim()) { setErr('A note is required.'); return; }
    setBusy(true);
    const r = await api.addNote(detail.id, note.trim());
    setBusy(false);
    if (!r.ok) { setErr(r.error || 'Could not add the note.'); return; }
    setNote(''); onChanged();
  }

  const isDone = detail.status === 'done';

  return (
    <div style={{ ...card }}>
      {detail.broke_glass && (
        <div style={{ marginBottom: 10, padding: '7px 10px', borderRadius: 8, background: C.redLight, border: `1px solid ${C.red}`, fontSize: 12, color: C.red, fontWeight: 600 }}>
          🔓 Opened via break-glass — this access has been recorded in the audit log and the task activity.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 16, margin: 0 }}>{detail.title}</h2>
        {detail.priority === 'high' && <Pill text="HIGH" bg={C.amberLight} fg={C.amber} />}
        {detail.overdue && <Pill text="OVERDUE" bg={C.redLight} fg={C.red} />}
        <VisibilityChip v={detail.visibility} />
        {detail.base && <Pill text={detail.base.short_code} bg={C.pill} fg={C.textMuted} />}
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, margin: '6px 0 10px' }}>
        {detail.source === 'email' && detail.created_by_email ? `From email: ${detail.created_by_email}` : `Created by ${detail.creator ? personRefLabel(detail.creator) : '—'}`}
        {detail.completed_at ? ` · completed ${fmt(detail.completed_at)}` : ''}
      </div>
      {detail.description && <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', background: C.pill, borderRadius: 8, padding: 10, marginBottom: 14 }}>{detail.description}</div>}

      {/* Mark complete / reopen */}
      <div style={{ marginBottom: 12 }}>
        {isDone
          ? <Btn kind="ghost" disabled={busy} onClick={() => patch({ status: 'open' }, 'Could not reopen.')}>Reopen task</Btn>
          : <Btn kind="primary" disabled={busy} onClick={() => patch({ status: 'done' }, 'Could not complete.')}>✓ Mark complete</Btn>}
      </div>

      {/* Controls */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        <div><label style={labelStyle}>Status</label>
          <select value={detail.status} disabled={busy} onChange={(e) => patch({ status: e.target.value as api.OfficeTaskStatus }, 'Update failed.')} style={fullInput}>
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div><label style={labelStyle}>Priority</label>
          <select value={detail.priority} disabled={busy} onChange={(e) => patch({ priority: e.target.value as api.OfficeTaskPriority }, 'Update failed.')} style={fullInput}>
            <option value="normal">Normal</option><option value="high">High</option>
          </select>
        </div>
        <div><label style={labelStyle}>Base</label>
          <select value={detail.base_id ?? ''} disabled={busy} onChange={(e) => patch({ base_id: e.target.value || null }, 'Could not update the base.')} style={fullInput}>
            <option value="">No base</option>
            {bases.map((b) => <option key={b.id} value={b.id}>{b.short_code} — {b.name}</option>)}
          </select>
        </div>
        <div><label style={labelStyle}>Visible to</label>
          <select value={detail.visibility} disabled={busy} onChange={(e) => patch({ visibility: e.target.value as api.OfficeTaskVisibility }, 'Could not update visibility.')} style={fullInput}>
            <option value="personal">Personal (creator + assignees + chain)</option>
            <option value="base">Base (everyone at the task's base)</option>
            <option value="company">Company (all staff)</option>
          </select>
        </div>
        <div><label style={labelStyle}>Due date</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="datetime-local" value={due} disabled={busy} onChange={(e) => setDue(e.target.value)} style={{ ...fullInput }} />
            <Btn kind="ghost" disabled={busy} onClick={saveDue}>Save</Btn>
          </div>
        </div>
      </div>

      {/* Assignees */}
      <label style={labelStyle}>Assignees</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {detail.assignees.length === 0 && <span style={{ fontSize: 12.5, color: C.textMuted }}>Nobody assigned yet.</span>}
        {detail.assignees.map((a) => (
          <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, background: C.pill, borderRadius: 999, padding: '3px 8px' }}>
            {personRefLabel(a.user)}
            <button onClick={() => removeAssignee(a.user_id)} disabled={busy} title="Remove" style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 14, lineHeight: 1 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ maxWidth: 320, marginBottom: 16 }}>
        <PersonCombobox placeholder="Add an assignee…" options={pickerOptions} onSelect={addAssignee} />
      </div>

      {/* Comment box */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginBottom: 14 }}>
        <h3 style={{ fontSize: 13.5, margin: '0 0 8px' }}>Add a note</h3>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a comment to the activity log…"
          style={{ ...fullInput, minHeight: 54, resize: 'vertical', marginBottom: 8 }} />
        <Btn kind="primary" disabled={busy} onClick={submitNote}>Add note</Btn>
        <ErrorText>{err}</ErrorText>
      </div>

      {/* Timeline */}
      <h3 style={{ fontSize: 13.5, margin: '0 0 8px' }}>Activity</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {detail.actions.length === 0 && <span style={{ fontSize: 12.5, color: C.textMuted }}>No activity yet.</span>}
        {[...detail.actions].reverse().map((a) => {
          const label = actionActorLabel(a);
          const accent = a.action_type === 'reminder' ? C.amber : a.action_type === 'completed' ? C.green : C.border;
          return (
            <div key={a.id} style={{ fontSize: 12.5, borderLeft: `3px solid ${accent}`, paddingLeft: 10 }}>
              <div style={{ color: C.textMuted, fontSize: 11 }}>{a.action_type.replace(/_/g, ' ')} · {fmt(a.created_at)}{a.actor_id ? '' : ' · system'}</div>
              {a.body && <div style={{ color: C.text }}><strong>{label}:</strong> {a.body}</div>}
              {!a.body && <div style={{ color: C.textDim }}><strong>{label}</strong></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── New task modal ──────────────────────────────────────────────────────────────────
function NewTask({ staff, bases, onClose, onCreated, setErr, err }: { staff: api.StaffOption[]; bases: api.BaseRef[]; onClose: () => void; onCreated: (id: string) => void; setErr: (s: string) => void; err: string }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<api.OfficeTaskPriority>('normal');
  const [due, setDue] = useState('');
  const [baseId, setBaseId] = useState('');
  const [assignees, setAssignees] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // Visibility: 'auto' follows the 3c default (assignees → personal; else base → base; else company)
  // and updates live as the form changes; the creator can override to a fixed scope.
  const [visibility, setVisibility] = useState<api.OfficeTaskVisibility | 'auto'>('auto');
  const default3c: api.OfficeTaskVisibility = assignees.length ? 'personal' : baseId ? 'base' : 'company';
  const effectiveVis = visibility === 'auto' ? default3c : visibility;

  const assignedIds = new Set(assignees);
  const pickerOptions: PersonOption[] = useMemo(
    () => staff.filter((s) => !assignedIds.has(s.id)).map((s) => ({
      id: s.id, label: staffLabel(s), code: s.pilot_code, first_name: s.first_name, last_name: s.last_name, full_name: s.full_name,
    })),
    [staff, assignees],
  );
  const staffById = useMemo(() => new Map(staff.map((s) => [s.id, s])), [staff]);

  async function submit() {
    setErr('');
    if (!title.trim()) { setErr('A title is required.'); return; }
    if (effectiveVis === 'base' && !baseId) { setErr('Choose a base for base-scoped visibility.'); return; }
    setBusy(true);
    const r = await api.createTask({
      title: title.trim(),
      description: description.trim() || null,
      priority,
      due_at: due ? new Date(due).toISOString() : null,
      base_id: baseId || null,
      assignee_user_ids: assignees.length ? assignees : undefined,
      // Send only an explicit override; 'auto' lets the server apply the same 3c default.
      visibility: visibility === 'auto' ? undefined : visibility,
    });
    setBusy(false);
    if (!r.ok || !r.id) { setErr(r.error || 'Could not create the task.'); return; }
    onCreated(r.id);
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: 560, maxWidth: '100%', fontFamily: F, color: C.text }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>New task</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, color: C.textMuted, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ marginBottom: 10 }}><label style={labelStyle}>Title <span style={{ color: C.red }}>*</span></label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Order more headsets" style={fullInput} /></div>
        <div style={{ marginBottom: 10 }}><label style={labelStyle}>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...fullInput, minHeight: 70, resize: 'vertical' }} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value as api.OfficeTaskPriority)} style={fullInput}>
              <option value="normal">Normal</option><option value="high">High</option>
            </select>
          </div>
          <div><label style={labelStyle}>Due date (optional)</label>
            <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} style={fullInput} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
          <div><label style={labelStyle}>Base (optional)</label>
            <select value={baseId} onChange={(e) => setBaseId(e.target.value)} style={fullInput}>
              <option value="">No base</option>
              {bases.map((b) => <option key={b.id} value={b.id}>{b.short_code} — {b.name}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>Visible to</label>
            <select value={effectiveVis} onChange={(e) => setVisibility(e.target.value as api.OfficeTaskVisibility)} style={fullInput}>
              <option value="personal">Personal (creator + assignees)</option>
              <option value="base">Base (everyone at the base)</option>
              <option value="company">Company (all staff)</option>
            </select>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3 }}>
              {visibility === 'auto' ? `Defaulted to ${api.VISIBILITY_LABELS[default3c]} — change to override.` : 'Overridden.'}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>Assignees</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
            {assignees.map((id) => {
              const s = staffById.get(id);
              return (
                <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, background: C.pill, borderRadius: 999, padding: '3px 8px' }}>
                  {s ? staffLabel(s) : id}
                  <button onClick={() => setAssignees((a) => a.filter((x) => x !== id))} title="Remove" style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 14, lineHeight: 1 }}>×</button>
                </span>
              );
            })}
          </div>
          <div style={{ maxWidth: 320 }}>
            <PersonCombobox placeholder="Add an assignee…" options={pickerOptions} onSelect={(id) => setAssignees((a) => (a.includes(id) ? a : [...a, id]))} />
          </div>
        </div>
        <ErrorText>{err}</ErrorText>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" disabled={busy} onClick={submit}>Create task</Btn>
        </div>
      </div>
    </div>
  );
}
